import { useState, useEffect, memo } from 'react';
import styles from './AnimatedBalance.module.css';

interface AnimatedBalanceProps {
  value: number;
  precision?: number;
  duration?: number;
  className?: string;
  showAnimation?: boolean;
}

const AnimatedBalance = memo(({ 
  value, 
  precision = 2, 
  duration = 1000,
  className = '',
  showAnimation = false
}: AnimatedBalanceProps) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousValue, setPreviousValue] = useState(value);
  const [difference, setDifference] = useState(0);

  useEffect(() => {
    if (value !== previousValue) {
      const newDifference = value - previousValue;
      setDifference(newDifference);
      setIsAnimating(true);
      
      const startTime = Date.now();
      
      const animate = () => {
        const currentTime = Date.now();
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = previousValue + (newDifference * easeOutQuart);
        
        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(value);
          setIsAnimating(false);
          setPreviousValue(value);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [value, duration, previousValue]);

  return (
    <div className={`${styles.balanceWrapper} ${className}`}>
      <span className={styles.value}>
        {displayValue.toFixed(precision)}
      </span>
      {showAnimation && isAnimating && difference > 0 && (
        <span className={styles.difference}>
          +{difference.toFixed(precision)}
        </span>
      )}
    </div>
  );
});

AnimatedBalance.displayName = 'AnimatedBalance';

export default AnimatedBalance; 